"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useTranslations } from "next-intl"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import type { ComicDraft } from "@kraahl/contract"
import type { StepProps } from "@/types/listing"
import { motion } from "framer-motion"
import { useMobileInputScroll } from "@/hooks/use-mobile-input-scroll"
import {
  creationFlowInputClass,
  creationFlowBlockClass,
  creationFlowMutedClass,
  creationFlowSectionLabelClass,
  creationFlowTextareaClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import { DetailsStep } from "@/components/publications/creation-flow/listing-creation/steps/details-step"

const MIN_PUBLICATION_YEAR = 1900
const MAX_PUBLICATION_YEAR = 2100

export function ComicDetailsStep({
  data,
  updateDataAction,
  onValidationChange,
  onRegisterSave,
  hideButton,
  compact,
  showListingTypeSection,
  showSaleTypeSection,
  allowAuctionOption,
  hideQuantity,
}: StepProps & {
  showListingTypeSection?: boolean
  showSaleTypeSection?: boolean
  allowAuctionOption?: boolean
  hideQuantity?: boolean
}) {
  const t = useTranslations("comicCreation.details")
  // signatureSeries / signatureDetails labels live under the condition
  // namespace in the agreed key map — the fields render here.
  const tCondition = useTranslations("comicCreation.condition")
  const draft = data.comicDraft

  const [issueTitle, setIssueTitle] = useState(draft?.issueTitle ?? "")
  const [issueNumber, setIssueNumber] = useState(draft?.issueNumber ?? "")
  const [publicationYear, setPublicationYear] = useState(
    draft?.publicationYear?.toString() ?? ""
  )
  const [coverVariant, setCoverVariant] = useState(draft?.coverVariant ?? "")
  const [isKeyIssue, setIsKeyIssue] = useState(draft?.isKeyIssue ?? false)
  const [keyNotes, setKeyNotes] = useState(draft?.keyNotes ?? "")
  const [isSignatureSeries, setIsSignatureSeries] = useState(
    draft?.isSignatureSeries ?? false
  )
  const [signatureDetails, setSignatureDetails] = useState(
    draft?.signatureDetails ?? ""
  )
  const [notes, setNotes] = useState(draft?.notes ?? "")
  const [commerceValid, setCommerceValid] = useState(true)

  const titleInputRef = useMobileInputScroll<HTMLInputElement>()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const yearNum = publicationYear ? parseInt(publicationYear, 10) : undefined
  const yearValid =
    publicationYear === "" ||
    (yearNum !== undefined &&
      yearNum >= MIN_PUBLICATION_YEAR &&
      yearNum <= MAX_PUBLICATION_YEAR)
  const isValid = Boolean(commerceValid && yearValid)

  useEffect(() => {
    onValidationChange?.(isValid)
  }, [isValid, onValidationChange])

  const saveData = useCallback(() => {
    const comicDraft: ComicDraft = {
      ...(draft ?? {}),
      issueTitle: issueTitle.trim() || null,
      issueNumber: issueNumber.trim() || null,
      publicationYear: publicationYear ? parseInt(publicationYear, 10) : null,
      coverVariant: coverVariant.trim() || null,
      isKeyIssue,
      keyNotes: isKeyIssue ? keyNotes.trim() || null : null,
      isSignatureSeries,
      signatureDetails: isSignatureSeries ? signatureDetails.trim() || null : null,
      notes: notes.trim() || null,
    }
    // Mirror of the cardName → title pattern: the issue title doubles as the
    // listing title. It is optional for comics, so an empty value leaves
    // `data.title` untouched instead of blanking it.
    const trimmedTitle = issueTitle.trim()
    updateDataAction(
      trimmedTitle ? { comicDraft, title: trimmedTitle } : { comicDraft }
    )
  }, [
    draft, issueTitle, issueNumber, publicationYear, coverVariant,
    isKeyIssue, keyNotes, isSignatureSeries, signatureDetails, notes,
    updateDataAction,
  ])

  useEffect(() => {
    onRegisterSave?.(saveData)
    return () => { onRegisterSave?.(null) }
  }, [saveData, onRegisterSave])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(saveData, 600)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [issueTitle, saveData])

  const handleCommerceValidation = useCallback((valid: boolean) => {
    setCommerceValid(valid)
  }, [])

  const sectionLabel = creationFlowSectionLabelClass
  const blockBase = creationFlowBlockClass
  const blockPadding = compact ? "p-3" : "p-4"
  const inputBase = creationFlowInputClass
  const mutedText = creationFlowMutedClass

  return (
    <div className={compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"}>
      <motion.div className={compact ? "space-y-4" : "space-y-5"} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>

        <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
          <p className={cn(sectionLabel, "mb-3")}>{t("title")}</p>
          <div className="space-y-3">
            <div>
              <Input
                ref={titleInputRef}
                placeholder={t("issueTitle")}
                value={issueTitle}
                onChange={(e) => setIssueTitle(e.target.value)}
                maxLength={200}
                className={inputBase}
              />
              <div className={cn("text-xs text-right mt-1", mutedText)}>{issueTitle.length}/200</div>
            </div>
            <Input
              placeholder={t("issueNumber")}
              value={issueNumber}
              onChange={(e) => setIssueNumber(e.target.value)}
              maxLength={40}
              className={inputBase}
            />
            <div>
              <Input
                placeholder={t("publicationYear")}
                type="text"
                inputMode="numeric"
                value={publicationYear}
                onChange={(e) => {
                  if (e.target.value === "" || /^\d{0,4}$/.test(e.target.value)) setPublicationYear(e.target.value)
                }}
                className={inputBase}
              />
              {!yearValid && (
                <p className="text-xs text-red-400 mt-1">
                  {MIN_PUBLICATION_YEAR}–{MAX_PUBLICATION_YEAR}
                </p>
              )}
            </div>
            <Input
              placeholder={t("coverVariant")}
              value={coverVariant}
              onChange={(e) => setCoverVariant(e.target.value)}
              maxLength={200}
              className={inputBase}
            />
          </div>
        </div>

        <DetailsStep
          data={data}
          updateDataAction={updateDataAction}
          onNextAction={() => {}}
          onPrevAction={() => {}}
          hideButton
          commerceOnly
          compact={compact}
          hideQuantity={hideQuantity}
          showListingTypeSection={showListingTypeSection}
          showSaleTypeSection={showSaleTypeSection}
          allowAuctionOption={allowAuctionOption}
          onValidationChange={handleCommerceValidation}
        />

        <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">{t("keyIssue")}</p>
            <div>
              <Switch checked={isKeyIssue} onCheckedChange={setIsKeyIssue} />
            </div>
          </div>
          {isKeyIssue && (
            <Textarea
              placeholder={t("keyNotes")}
              value={keyNotes}
              onChange={(e) => setKeyNotes(e.target.value)}
              maxLength={500}
              rows={2}
              className={cn("mt-3 min-h-[60px] resize-none", creationFlowTextareaClass)}
            />
          )}
        </div>

        <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">{tCondition("signatureSeries")}</p>
            <div>
              <Switch checked={isSignatureSeries} onCheckedChange={setIsSignatureSeries} />
            </div>
          </div>
          {isSignatureSeries && (
            <div className="mt-3">
              <Input
                placeholder={tCondition("signatureDetails")}
                value={signatureDetails}
                onChange={(e) => setSignatureDetails(e.target.value)}
                maxLength={200}
                className={inputBase}
              />
            </div>
          )}
        </div>

        <div className={cn(blockPadding, "rounded-2xl", blockBase)}>
          <p className={cn(sectionLabel, "mb-3")}>{t("notes")}</p>
          <Textarea
            placeholder={t("notes")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={3}
            className={cn("min-h-[80px] resize-none", creationFlowTextareaClass)}
          />
        </div>
      </motion.div>
    </div>
  )
}
